import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, Brain, Check, X, Clock, Coins, Zap, Sparkles } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import TopBar from "../components/TopBar";

export default function Review() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const t = useT();
  const [data, setData] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [results, setResults] = useState([]);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/review").then((r) => setData(r.data)).catch(() => {}); }, []);

  const answer = (choice) => {
    if (revealed) return;
    const q = data.due[idx];
    setPicked(choice); setRevealed(true);
    setResults([...results, { q: q.q, correct: choice === q.answer }]);
  };

  const next = async () => {
    if (idx + 1 >= data.due.length) {
      const { data: res } = await api.post("/review/submit", { results });
      setUser(res.user); setDone(res);
      return;
    }
    setIdx(idx + 1); setPicked(null); setRevealed(false);
  };

  if (!data) return <div className="space-bg min-h-screen flex items-center justify-center text-white/60">{t("Caricamento...")}</div>;

  if (done) {
    return (
      <div className="space-bg min-h-screen flex items-center justify-center px-5">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="glass rounded-3xl p-8 max-w-md text-center" data-testid="review-done-card">
          <Brain size={48} style={{ color: "var(--pink)" }} className="mx-auto mb-3" />
          <h2 className="font-heading font-black text-2xl tracking-tight mb-2">{t("Ripasso completato!")}</h2>
          <p className="text-white/60 mb-4">{done.correct}/{results.length} {t("corrette. Le domande sbagliate torneranno tra qualche giorno.")}</p>
          <div className="flex justify-center gap-6 font-num mb-6">
            <span className="flex items-center gap-1"><Zap size={18} style={{ color: "var(--cyan)" }} /> +{done.xp_earned} XP</span>
          </div>
          <button onClick={() => navigate("/dashboard")} className="w-full py-3 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="review-home-btn">{t("Torna alla Home")}</button>
        </motion.div>
      </div>
    );
  }

  if (!playing) {
    return (
      <div className="space-bg min-h-screen pb-20">
        <TopBar />
        <div className="max-w-lg mx-auto px-4 pt-5">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-white/60 bouncy mb-4" data-testid="review-back-btn"><ChevronLeft size={18} /> {t("Home")}</button>
          <h1 className="font-heading font-black text-3xl tracking-tight flex items-center gap-2 mb-2"><Brain style={{ color: "var(--pink)" }} /> {t("Ripasso Intelligente")}</h1>
          <p className="text-white/60 text-sm mb-6">{t("Riproponiamo le domande che hai sbagliato, a distanza crescente di giorni (1, 3, 7, 16) per fissarle nella memoria.")}</p>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="glass rounded-2xl p-4 text-center"><div className="font-num text-3xl" style={{ color: "var(--pink)" }}>{data.due_count}</div><div className="text-xs text-white/50">{t("da ripassare")}</div></div>
            <div className="glass rounded-2xl p-4 text-center"><div className="font-num text-3xl" style={{ color: "var(--cyan)" }}>{data.upcoming}</div><div className="text-xs text-white/50">{t("in attesa")}</div></div>
            <div className="glass rounded-2xl p-4 text-center"><div className="font-num text-3xl text-white/80">{data.total}</div><div className="text-xs text-white/50">{t("totali")}</div></div>
          </div>

          {data.due_count > 0 ? (
            <button onClick={() => setPlaying(true)} className="w-full py-4 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="start-review-btn">{t("Inizia ripasso")} ({data.due_count})</button>
          ) : (
            <div className="glass rounded-2xl p-6 text-center text-white/60" data-testid="no-review-msg">
              <Sparkles size={32} style={{ color: "var(--success)" }} className="mx-auto mb-2" />
              {data.total === 0 ? t("Nessuna domanda da ripassare ancora. Gioca qualche livello!") : t("Tutto ripassato per ora! Torna più tardi. 🎉")}
            </div>
          )}
        </div>
      </div>
    );
  }

  const q = data.due[idx];
  return (
    <div className="space-bg min-h-screen flex flex-col">
      <header className="glass sticky top-0 z-40 px-4 py-3 flex items-center justify-between">
        <button onClick={() => navigate("/dashboard")} className="text-white/60 bouncy"><X size={22} /></button>
        <span className="font-num text-sm text-white/70">{idx + 1}/{data.due.length}</span>
        <Brain size={20} style={{ color: "var(--pink)" }} />
      </header>
      <div className="flex-1 max-w-lg w-full mx-auto px-4 py-6 flex flex-col">
        <span className="text-xs uppercase tracking-widest text-white/40 font-heading mb-3">{q.subject}</span>
        <motion.div key={idx} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} className="glass rounded-3xl p-6 mb-5" data-testid="review-question">
          <p className="font-heading font-bold text-lg tracking-tight">{q.q}</p>
        </motion.div>
        <div className="space-y-3 flex-1">
          {q.o.map((opt, oi) => {
            const isCorrect = revealed && oi === q.answer;
            const isWrong = revealed && oi === picked && oi !== q.answer;
            return (
              <motion.button key={oi} whileTap={{ scale: 0.97 }} onClick={() => answer(oi)} disabled={revealed}
                className="w-full text-left px-5 py-4 rounded-2xl font-body font-semibold flex items-center justify-between glass"
                style={{ border: isCorrect ? "2px solid var(--success)" : isWrong ? "2px solid var(--danger)" : "1px solid rgba(255,255,255,0.12)",
                  background: isCorrect ? "rgba(0,255,127,0.15)" : isWrong ? "rgba(255,51,102,0.15)" : undefined }}
                data-testid={`review-answer-${oi}`}>
                <span>{opt}</span>
                {isCorrect && <Check size={20} style={{ color: "var(--success)" }} />}
                {isWrong && <X size={20} style={{ color: "var(--danger)" }} />}
              </motion.button>
            );
          })}
        </div>
        <AnimatePresence>
          {revealed && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass rounded-2xl p-4 mt-4" data-testid="review-explanation">
              <p className="text-sm text-white/70">{q.explanation}</p>
              <button onClick={next} className="w-full mt-3 py-3 rounded-2xl btn-neon font-heading font-bold bouncy" data-testid="review-next-btn">
                {idx + 1 >= data.due.length ? t("Termina") : t("Prossima")}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
